import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Target, Wrench, Brain, Star, DollarSign, Award, BarChart3, MessageCircle, Rocket
} from "lucide-react";
import { useDispatch } from "react-redux";
import { setTargetJob, setTargetJobDescription } from "../../redux/userSlice";
import { api1 } from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import CareerBookmarkButton from "../careerBookmarkButton/CareerBookmarkButton";

const CareerDetails = ({ career, isExpanded, userId, isSaved = false, onBookmarkChange }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [expandedSkills, setExpandedSkills] = useState({});
  const [modalItem, setModalItem] = useState(null);
  const [showCareerSelectedModal, setShowCareerSelectedModal] = useState(false);
  const [isSelectingCareer, setIsSelectingCareer] = useState(false);

  const toggleSkillsExpansion = (index) => {
    setExpandedSkills((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handleSelectCareer = async () => {
    setIsSelectingCareer(true);
    const targetJobAction = setTargetJob(career.name);
    const targetJobDescAction = setTargetJobDescription(career.description);
    dispatch(targetJobAction);
    dispatch(targetJobDescAction);    
    const targetCareerType = isMainstreamCareer ? "mainstream" : "emerging";
    
    try {
      await api1.patch(`${API_ENDPOINTS.UPDATE_PROFILE}/${userId}`, { 
        targetJob: career.name, 
        targetJobDescription: career.description,
        targetCareerType
      });
      
      setShowCareerSelectedModal(true);
    } catch (err) {
      console.error("Selection error:", err);
    } finally {
      setIsSelectingCareer(false);
    }
  };

  const isMainstreamCareer = career.necessarySkills && !career.tools_and_technologies;
  
  const skillSections = [
    { 
      icon: Wrench, 
      title: isMainstreamCareer ? "Necessary Skills" : "Tools and Technologies", 
      data: isMainstreamCareer ? career.necessarySkills : career.tools_and_technologies, 
      descKey: isMainstreamCareer ? "description" : "use_case_in_role", 
      gradient: "from-purple-500 to-blue-500" 
    },
    { icon: Target, title: "Common Skills", data: career.commonSkills, descKey: "description", gradient: "from-purple-500 to-pink-500" },
    { icon: Brain, title: "Defining Skills", data: career.definingSkills, descKey: "description", gradient: "from-purple-500 to-indigo-500" },
    { icon: Star, title: "Distinguishing Skills", data: career.distinguishingSkills, descKey: "description", gradient: "from-purple-500 to-yellow-500" },
    { icon: DollarSign, title: "Salary Boosting Skills", data: career.salaryBoostingSkills, descKey: "description", gradient: "from-purple-500 to-pink-500" },
    { icon: Award, title: "Certificates", data: career.certifications, descKey: null, gradient: "from-purple-500 to-blue-500" }
  ];

  if (!isExpanded) return null;

  return (
    <div className="bg-gray-50 rounded-lg px-4 mb-4 space-y-6">
      {/* Modal */}
      {modalItem && (
        <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md relative">
            <button onClick={() => setModalItem(null)} className="absolute top-2 right-3 text-xl text-gray-400 hover:text-gray-600">✕</button>
            <h2 className="text-xl font-bold mb-2">{modalItem.name}</h2>
            <p className="text-sm text-gray-700">{modalItem.description || modalItem.use_case_in_role || "No description available."}</p>
          </div>
        </div>
      )}

      {/* Career Selected Modal */}
      {showCareerSelectedModal && (
        <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md relative">
            <h2 className="text-xl font-bold mb-4 text-center">Your Target Career has been Selected</h2>
            <p className="text-sm text-gray-700 mb-6 text-center">
              You have selected '{career.name}' as your target career. What would you like to do next?
            </p>
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowCareerSelectedModal(false);
                  navigate("/dashboard/career-assessment/ai-literacy-assessment");
                }}
                className="w-full py-2 px-4 border border-blue-500 text-blue-500 rounded-lg hover:bg-blue-50 transition-colors"
              >
                Start AI Literacy Assessment
              </button>
              <button
                onClick={() => {
                  setShowCareerSelectedModal(false);
                  navigate("/dashboard/skills-and-learning/upskilling-pathway-config");
                }}
                className="w-full py-2 px-4 border border-blue-500 text-blue-500 rounded-lg hover:bg-blue-50 transition-colors"
              >
                Skip Assessment and Generate Upskilling Pathway <span className="text-red-500">(Not Advisable)</span>
              </button>
              <button
                onClick={() => setShowCareerSelectedModal(false)}
                className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Career Description Section */}
      {career?.description && (
        <p className="text-base text-gray-600 leading-relaxed">{career.description}</p>
      )}

      {career?.transition_rationale && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center">
              <Target className="w-4 h-4 text-white" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900">Transition Rationale - Why this is a relevant career move</h4>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">{career.transition_rationale}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {skillSections.map(({ icon: Icon, title, data, descKey, gradient }, idx) => (
          <div key={idx} className="bg-white border border-gray-300 p-4 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-8 h-8 bg-gradient-to-r ${gradient} rounded-lg flex items-center justify-center`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <h5 className="font-medium text-base text-gray-900">{title}</h5>
            </div>
            <div className="text-xs text-gray-600">
              {Array.isArray(data) && data.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-1">
                    {data.slice(0, 3).map((item, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 cursor-pointer transition-colors"
                        onClick={() => typeof item === "object" && item.name && setModalItem(item)}
                      >
                        {typeof item === "string" ? item : item.name}
                      </span>
                    ))}
                    {data.length > 3 && !expandedSkills[idx] && (
                      <span 
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer transition-colors"
                        onClick={() => toggleSkillsExpansion(idx)}
                      >
                        +{data.length - 3} more
                      </span>
                    )}
                  </div>
                  {data.length > 3 && expandedSkills[idx] && (
                    <div className="flex flex-wrap gap-1">
                      {data.slice(3).map((item, i) => (
                        <span
                          key={i + 3}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 cursor-pointer transition-colors"
                          onClick={() => typeof item === "object" && item.name && setModalItem(item)}
                        >
                          {typeof item === "string" ? item : item.name}
                        </span>
                      ))}
                      <span 
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer transition-colors"
                        onClick={() => toggleSkillsExpansion(idx)}
                      >
                        Show Less
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-gray-500 italic">No skills available</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <button
          onClick={handleSelectCareer}
          disabled={isSelectingCareer}
          className="bg-white text-gray-700 border border-gray-300 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isSelectingCareer && (
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-purple-600"></div>
          )}
          {isSelectingCareer ? "Selecting..." : "Select This Career"}
        </button>
        <CareerBookmarkButton
          career={career}
          size="md"
          variant="outline"
          isBookmarked={isSaved}
          onBookmarkChange={(isBookmarked, career) => {
            if (onBookmarkChange) {
              onBookmarkChange();
            }
          }}
        />
      </div>

      {/* Career Actions */}
      <div className="space-y-3">
        <p className="text-xl font-bold text-blue-900">Career Actions</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button
            onClick={() => navigate("/dashboard/skills-and-learning/skill-gap-analysis-tool")}
            className="w-full text-xs py-3 px-3 rounded-lg font-medium flex items-center justify-center gap-2 border bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          >
            <BarChart3 className="w-4 h-4 text-purple-600" />
            Conduct Skills Gap
          </button>
          <button
            onClick={() => navigate("/dashboard/career-discovery/career-simulation-tool")}
            className="w-full text-xs py-3 px-3 rounded-lg font-medium flex items-center justify-center gap-2 border bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          >
            <Brain className="w-4 h-4 text-purple-600" />
            Simulate this Career
          </button>
          <button
            onClick={() => console.log("Mock interview:", career.name)}
            className="w-full text-xs py-3 px-3 rounded-lg font-medium flex items-center justify-center gap-2 border bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          >
            <MessageCircle className="w-4 h-4 text-purple-600" />
            Do a Mock Interview
          </button>
        </div>
      </div>
    </div>
  );
};

export default CareerDetails;
